import styled from 'styled-components'
import MockExam from 'assets/ai-enhanced/mock-exam-smart-tool.svg'
import Quiz from 'assets/ai-enhanced/quiz-smart-tool.svg'
import Flashcards from 'assets/ai-enhanced/flashcards-smart-tool.svg'
import Essay from 'assets/ai-enhanced/essay-smart-tool.svg'
import theme from 'stories/utils/theme'
import { Body1, Body2 } from 'stories/elements/Typography/Text'
import { AnalyticsEventKnowPageSmartToolsClick } from 'lib/constants/AnalyticsEventType'
import { trackEvent } from 'lib/tracking/analytics-service'
import { appDeepLinkKnow } from 'lib/features/app-deep-links'
import { Know } from 'interfaces/Know'
import { redirectToAppDownloadUrlAsync } from 'lib/features/app-links'
import _useTranslation from 'lib/hooks/_useTranslation'
import { parseTranslationInterpolation } from 'lib/features/translation'

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    .grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;
    }
    padding: 0px 5% 25px 5%;
    .transform-note {
        font-family: var(--font-inter);
        font-weight: 400;
        font-size: 17px;
        line-height: 26px;
        letter-spacing: 0.3px;
        color: ${theme.colors.white};
        span {
            all: inherit;
            padding: 0px;

            &::first-letter {
                color: rgba(94, 208, 98, 1);
                padding-right: 4px;
            }
        }
    }
    .smart-tools-title {
        display: flex;
        align-items: center;
        gap: 10px;
        font-family: var(--font-inter);
        font-weight: 750;
        font-size: 23px;
        line-height: 28px;
        letter-spacing: -0.2px;
        color: ${theme.colors.white};
        span {
            font-family: var(--font-inter);
            font-weight: 650;
            font-size: 11px;
            line-height: 16px;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            background-color: ${theme.colors.white};
            padding: 5px 10px;
            border-radius: 28px;
            color: ${theme.colors.black};
        }
    }
`

const Card = styled.div`
    border-radius: 24px;
    background: radial-gradient(50% 50% at 50% 0%, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%),
        ${(props) => props.color};
    width: 100%;
    height: 130px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 10px;
    cursor: pointer;

    .title {
        font-family: var(--font-inter);
        font-size: 17px;
        font-weight: 550;
        letter-spacing: 0.3px;
        line-height: 20px;
        color: ${theme.colors.white};
    }
`

const StyledSpan = styled.span``

interface Props {
    know: Know
    referrerScreen?: string
    onClick?: () => void
}

const SmartToolsSection = ({ know, referrerScreen, onClick }: Props) => {
    const { t: tKnows } = _useTranslation('knows')
    const { t: tCommon } = _useTranslation('common')

    const cards = [
        {
            color: '#004677',
            title: tKnows('knows/mockExamTitle'),
            type: 'mock_exam',
            asset: <MockExam />,
        },
        {
            color: '#3a1d6d',
            title: tKnows('knows/quizSectionLabel'),
            type: 'quiz',
            asset: <Quiz />,
        },
        {
            color: '#143515',
            title: tCommon('common/navBarFlashcardMenuItem'),
            type: 'flashcards',
            asset: <Flashcards />,
        },
        {
            color: '#4b2203',
            title: tKnows('knows/essayTitle'),
            type: 'essay',
            asset: <Essay />,
        },
    ]

    const onCardClick = (type: string) => {
        trackEvent(AnalyticsEventKnowPageSmartToolsClick, {
            value: type,
            referrerScreen,
        })
        const downloadDeepLinkPath = appDeepLinkKnow(know.uuid)

        if (onClick) {
            onClick()
        } else {
            redirectToAppDownloadUrlAsync('dynamic', 'know-mobile-ai-enhanced-hero', downloadDeepLinkPath)
        }
    }

    return (
        <Container>
            <Body1 className="smart-tools-title" fontFamily="var(--font-inter)" fontWeight={550} fontSize={17}>
                {parseTranslationInterpolation(tKnows('knows/smartToolsSectionTitle'), [
                    {
                        index: 1,
                        component: StyledSpan,
                        props: {
                            color: theme.colors.deepSeaBlue,
                        },
                    },
                ])}
            </Body1>
            <Body2 className="transform-note">
                {parseTranslationInterpolation(tKnows('knows/smartToolsSectionTransformNote'), [
                    {
                        index: 1,
                        component: StyledSpan,
                        props: {
                            color: theme.colors.deepSeaBlue,
                        },
                    },
                    {
                        index: 3,
                        component: StyledSpan,
                        props: {
                            color: theme.colors.deepSeaBlue,
                        },
                    },
                    {
                        index: 5,
                        component: StyledSpan,
                        props: {
                            color: theme.colors.deepSeaBlue,
                        },
                    },
                    {
                        index: 7,
                        component: StyledSpan,
                        props: {
                            color: theme.colors.deepSeaBlue,
                        },
                    },
                ])}
                {/* Transform this note into:
                <span>✓ 50+ Practice Questions </span>
                <span>✓ Interactive Flashcards</span>
                <span>✓ Full Mock Exam </span>
                <span>✓ Essay Outlines</span> */}
            </Body2>
            <div className="grid">
                {cards.map((card) => (
                    <Card key={card.title} color={card.color} onClick={() => onCardClick(card.type)}>
                        {card.asset}
                        <div className="title">{card.title}</div>
                    </Card>
                ))}
            </div>
        </Container>
    )
}

export default SmartToolsSection
